import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  formatSubmissionStatusForAgentProfile,
  IMO,
  isSubmissionAutofillComplete,
  PINNACLE_AVAILABLE_CARRIERS,
} from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { formatRelativeDate } from "../lib/relativeDate";
import { useAuthenticatedAgent } from "./AuthenticatedLayout.js";
import { CreateAgentModal } from "./CreateAgentModal";
import { AgentSearchSelect } from "./fields/AgentSearchSelect";
import { AgentsListSkeleton } from "./skeletons/AgentsListSkeleton";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { FilterCard } from "./ui/FilterCard";
import { Input, Label } from "./ui/Input";
import { PageHeader } from "./ui/PageHeader";
import { StatusBadge } from "./ui/StatusBadge";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

type HierarchyReportKind = "contracted" | "status";

type AgentListSortBy = "name" | "contracts" | "updated" | "pending";

const PROVIDER_FILTERS = [
  { value: IMO.PINNACLE, label: "Pinnacle" },
  { value: IMO.PREMIER_PRODUCERS_GROUP, label: "PPG" },
  { value: IMO.HEARTLAND, label: "Heartland" },
  { value: IMO.SMS, label: "SMS" },
] as const;

const CONTRACT_STATUS_FILTERS = [
  { value: "PENDING", label: "Pending" },
  { value: "ACTIVE", label: "Active" },
  { value: "NIGO", label: "NIGO" },
  { value: "REJECTED", label: "Rejected" },
] as const;

type ContractStatusFilter = (typeof CONTRACT_STATUS_FILTERS)[number]["value"];

const STATUS_CHIP_CLASSES: Record<ContractStatusFilter, string> = {
  PENDING: "border-status-pending-border bg-status-pending-bg text-status-pending-text hover:opacity-90",
  ACTIVE: "border-status-active-border bg-status-active-bg text-status-active-text hover:opacity-90",
  NIGO: "border-status-nigo-border bg-status-nigo-bg text-status-nigo-text hover:opacity-90",
  REJECTED: "border-status-rejected-border bg-status-rejected-bg text-status-rejected-text hover:opacity-90",
};

const INACTIVE_CHIP_CLASS =
  "border-border-default bg-surface-overlay text-content-secondary hover:border-border-default hover:text-content-primary";

const ACTIVE_CHIP_CLASS =
  "border-[var(--color-accent-border)] bg-accent-muted text-content-primary hover:opacity-90";

const CLAIMED_FILTER_OPTIONS = [
  { value: "claimed", label: "Claimed" },
  { value: "unclaimed", label: "Unclaimed" },
] as const;

const PROVIDER_CHIP_CLASSES: Record<string, string> = {
  [IMO.PINNACLE]:
    "border-status-pending-border bg-status-pending-bg text-status-pending-text hover:opacity-90",
  [IMO.PREMIER_PRODUCERS_GROUP]:
    "border-[var(--color-accent-border)] bg-accent-muted text-content-primary hover:opacity-90",
  [IMO.HEARTLAND]:
    "border-status-nigo-border bg-status-nigo-bg text-status-nigo-text hover:opacity-90",
  [IMO.SMS]:
    "border-status-active-border bg-status-active-bg text-status-active-text hover:opacity-90",
};

const PROVIDER_ICON_CLASSES: Record<string, string> = {
  [IMO.PINNACLE]: "bg-[var(--status-pending-text)]",
  [IMO.PREMIER_PRODUCERS_GROUP]: "bg-[var(--color-accent)]",
  [IMO.HEARTLAND]: "bg-[var(--status-nigo-text)]",
  [IMO.SMS]: "bg-[var(--status-active-text)]",
};

interface AgentListItem {
  id: string;
  email: string;
  npn: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  organization: {
    id: string;
    name: string;
    brandName: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  carriers: string[];
  contractStats: {
    total: number;
    active: number;
    pending: number;
    nigo: number;
    rejected: number;
  };
  latestSubmission: {
    id: string;
    status: string;
  } | null;
  upline: {
    id: string;
    displayName: string;
    email: string;
  } | null;
  claimed: boolean;
}

type ClaimedFilter = "all" | "claimed" | "unclaimed";

function parseClaimedFilter(value: string | null): ClaimedFilter {
  if (value === "claimed" || value === "unclaimed") return value;
  return "all";
}

function ClaimedIcon({ claimed }: { claimed: boolean }) {
  return claimed ? (
    <svg
      className="size-4 text-status-active-text"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      role="img"
      aria-label="Claimed"
    >
      <title>Claimed</title>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  ) : (
    <svg
      className="size-4 text-content-muted"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      role="img"
      aria-label="Unclaimed"
    >
      <title>Unclaimed</title>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

const CARRIER_LABELS = new Map(PINNACLE_AVAILABLE_CARRIERS.map((c) => [c.value, c.label]));
const MAX_CARRIER_PILLS = 3;

function carrierLabel(value: string) {
  return CARRIER_LABELS.get(value) ?? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function submissionStatusVariant(status: string): "default" | "accent" | "outline" {
  const normalized = status.trim().toLowerCase();
  if (normalized === "failed") return "outline";
  if (normalized === "queued" || normalized === "processing") return "accent";
  return "default";
}

interface AgentListResponse {
  items: AgentListItem[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

function parseSortBy(value: string | null, isProviderViewer: boolean): AgentListSortBy {
  if (value === "name" || value === "contracts" || value === "updated" || value === "pending") {
    return value;
  }
  return isProviderViewer ? "pending" : "name";
}

function parseSortDir(value: string | null, sortBy: AgentListSortBy): "asc" | "desc" {
  if (value === "asc" || value === "desc") return value;
  if (sortBy === "updated" || sortBy === "contracts" || sortBy === "pending") return "desc";
  return "asc";
}

function parseImoFilter(value: string | null): string[] {
  if (!value) return [];
  return [...new Set(value.split(",").map((part) => part.trim()).filter(Boolean))];
}

function parseContractStatusFilter(value: string | null): ContractStatusFilter[] {
  if (!value) return [];
  const allowed = new Set(CONTRACT_STATUS_FILTERS.map((item) => item.value));
  return [
    ...new Set(
      value
        .split(",")
        .map((part) => part.trim().toUpperCase())
        .filter((part): part is ContractStatusFilter => allowed.has(part as ContractStatusFilter))
    ),
  ];
}

function useDebouncedSearchParam(
  paramKey: string,
  searchParams: URLSearchParams,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) {
  const value = searchParams.get(paramKey) ?? "";
  const [input, setInput] = useState(value);

  useEffect(() => {
    setInput(value);
  }, [value]);

  useEffect(() => {
    const trimmed = input.trim();
    if (trimmed === value) return;

    const timer = window.setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        if (trimmed) next.set(paramKey, trimmed);
        else next.delete(paramKey);
        return next;
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [input, value, paramKey, setSearchParams]);

  return [input, setInput] as const;
}

export function AgentsList({
  viewerRole,
  providerImo,
}: {
  viewerRole?: "ADMIN" | "PPG" | "PROVIDER";
  providerImo?: string | null;
}) {
  const sessionAgent = useAuthenticatedAgent();
  const effectiveRole = viewerRole ?? sessionAgent.role;
  const isProviderViewer = effectiveRole === "PROVIDER" || effectiveRole === "PPG";
  const canExportHierarchyReports =
    effectiveRole === "ADMIN" ||
    (Boolean(sessionAgent.isStaff) &&
      effectiveRole !== "PROVIDER" &&
      effectiveRole !== "PPG" &&
      effectiveRole !== "AGENT");
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AgentListResponse | null>(null);
  const [downloadingReport, setDownloadingReport] = useState<HierarchyReportKind | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const canCreateAgent = Boolean(sessionAgent.isStaff) && !isProviderViewer;
  const [searchInput, setSearchInput] = useDebouncedSearchParam("search", searchParams, setSearchParams);
  const [npnInput, setNpnInput] = useDebouncedSearchParam("npn", searchParams, setSearchParams);
  const [copiedNpnId, setCopiedNpnId] = useState<string | null>(null);
  const token = useMemo(() => getSessionToken(), []);

  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const search = searchParams.get("search") ?? "";
  const npn = searchParams.get("npn") ?? "";
  const uplineUserId = searchParams.get("uplineUserId") ?? "";
  const sortBy = parseSortBy(searchParams.get("sortBy"), isProviderViewer);
  const sortDir = parseSortDir(searchParams.get("sortDir"), sortBy);
  const selectedProviders = parseImoFilter(searchParams.get("imo"));
  const selectedStatuses = parseContractStatusFilter(searchParams.get("contractStatus"));
  const claimedFilter = parseClaimedFilter(searchParams.get("claimed"));
  const providerOptions = isProviderViewer
    ? PROVIDER_FILTERS.filter((option) =>
        providerImo ? option.value === providerImo : option.value === IMO.PREMIER_PRODUCERS_GROUP
      )
    : PROVIDER_FILTERS;

  async function loadAgents() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: String(page),
        pageSize: "25",
        sortBy,
        sortDir,
      });
      if (search) query.set("search", search);
      if (npn) query.set("npn", npn);
      if (uplineUserId) query.set("uplineUserId", uplineUserId);
      if (selectedProviders.length > 0) query.set("imo", selectedProviders.join(","));
      if (selectedStatuses.length > 0) query.set("contractStatus", selectedStatuses.join(","));
      if (claimedFilter !== "all") query.set("claimed", claimedFilter);

      const res = await fetch(`${API_BASE}/api/agents?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load agents");
      setData(json.data as AgentListResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load agents");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, search, npn, uplineUserId, sortBy, sortDir, selectedProviders.join(","), selectedStatuses.join(","), claimedFilter]);

  function toggleProvider(imo: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      const current = parseImoFilter(prev.get("imo"));
      const nextProviders = current.includes(imo)
        ? current.filter((value) => value !== imo)
        : [...current, imo];
      if (nextProviders.length > 0) next.set("imo", nextProviders.join(","));
      else next.delete("imo");
      return next;
    });
  }

  function toggleContractStatus(status: ContractStatusFilter) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      const current = parseContractStatusFilter(prev.get("contractStatus"));
      const nextStatuses = current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status];
      if (nextStatuses.length > 0) next.set("contractStatus", nextStatuses.join(","));
      else next.delete("contractStatus");
      return next;
    });
  }

  function clearContractStatuses() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      next.delete("contractStatus");
      return next;
    });
  }

  function setClaimedFilter(value: ClaimedFilter) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      if (value === "all") next.delete("claimed");
      else next.set("claimed", value);
      return next;
    });
  }

  function clearAllFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      next.delete("imo");
      next.delete("contractStatus");
      next.delete("uplineUserId");
      next.delete("search");
      next.delete("npn");
      next.delete("claimed");
      return next;
    });
    setSearchInput("");
    setNpnInput("");
  }

  const activeFilterCount =
    (search ? 1 : 0) +
    (npn ? 1 : 0) +
    (uplineUserId ? 1 : 0) +
    selectedProviders.length +
    selectedStatuses.length +
    (claimedFilter !== "all" ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  function clearProviders() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      next.delete("imo");
      return next;
    });
  }

  function toggleSort(key: AgentListSortBy) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      if (sortBy === key) {
        next.set("sortDir", sortDir === "asc" ? "desc" : "asc");
      } else {
        next.set("sortBy", key);
        next.set("sortDir", key === "updated" || key === "contracts" || key === "pending" ? "desc" : "asc");
      }
      return next;
    });
  }

  function sortIndicator(key: AgentListSortBy) {
    if (sortBy !== key) return null;
    return sortDir === "asc" ? "↑" : "↓";
  }

  function SortableHeader({ label, column }: { label: string; column: AgentListSortBy }) {
    const indicator = sortIndicator(column);
    return (
      <th className="text-left py-2 pr-3" aria-sort={sortBy === column ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
        <button
          type="button"
          onClick={() => toggleSort(column)}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-content-secondary transition-colors hover:text-content-primary"
        >
          {label}
          {indicator && <span className="text-accent">{indicator}</span>}
        </button>
      </th>
    );
  }

  async function copyNpn(agentId: string, npn: string) {
    try {
      await navigator.clipboard.writeText(npn);
      setCopiedNpnId(agentId);
      window.setTimeout(() => setCopiedNpnId((current) => (current === agentId ? null : current)), 1200);
    } catch {
      /* ignore */
    }
  }

  async function downloadHierarchyReport(kind: HierarchyReportKind) {
    const token = getSessionToken();
    if (!token) {
      setReportError("Sign in required to download reports.");
      return;
    }

    setDownloadingReport(kind);
    setReportError(null);

    try {
      const path =
        kind === "contracted"
          ? "/api/reports/hierarchy/contracted"
          : "/api/reports/hierarchy/status";
      const response = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        let message = "Failed to download report.";
        try {
          const json = (await response.json()) as { error?: string };
          if (json.error) message = json.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      anchor.download =
        kind === "contracted"
          ? `Altura-Contracted-Agent-Report-${stamp}.xlsx`
          : `Altura-Contracting-Status-Report-${stamp}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setReportError(err instanceof Error ? err.message : "Unable to download report.");
    } finally {
      setDownloadingReport(null);
    }
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title={isProviderViewer ? "Contract queue" : "Agents"}
        description={
          isProviderViewer
            ? "Agents with contracts and applications for your organization only."
            : "Insurance agents with submitted applications and provider contract records."
        }
        actions={
          canCreateAgent ? (
            <Button type="button" size="sm" onClick={() => setShowCreateAgent(true)}>
              New agent
            </Button>
          ) : undefined
        }
      />

      {canExportHierarchyReports && (
        <FilterCard>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-content-primary">Hierarchy database exports</h2>
              <p className="mt-1 text-sm text-content-secondary">
                Download Paul&apos;s Report A (contracted agents) or Report B (contracting status) as XLSX.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={downloadingReport !== null}
                onClick={() => downloadHierarchyReport("contracted")}
              >
                {downloadingReport === "contracted" ? "Generating…" : "Download Report A"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={downloadingReport !== null}
                onClick={() => downloadHierarchyReport("status")}
              >
                {downloadingReport === "status" ? "Generating…" : "Download Report B"}
              </Button>
            </div>
          </div>
        </FilterCard>
      )}

      {reportError && (
        <p className="text-sm text-status-rejected-text" role="alert">
          {reportError}
        </p>
      )}

      <FilterCard>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-content-muted">
              Filters
            </h2>
            {activeFilterCount > 0 && (
              <Badge variant="accent">{activeFilterCount} active</Badge>
            )}
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="min-h-11 px-2 text-xs text-accent hover:opacity-90"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Label>
            Name or email
            <Input
              className="mt-1.5"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name or email"
            />
          </Label>
          <Label>
            NPN
            <Input
              className="mt-1.5 font-mono"
              value={npnInput}
              onChange={(e) => setNpnInput(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 21489538"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </Label>
          {!isProviderViewer && token && (
            <Label>
              Upline
              <div className="mt-1.5">
                <AgentSearchSelect
                  value={uplineUserId}
                  token={token}
                  onChange={(nextUplineUserId) => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set("page", "1");
                      if (nextUplineUserId) next.set("uplineUserId", nextUplineUserId);
                      else next.delete("uplineUserId");
                      return next;
                    });
                  }}
                />
              </div>
            </Label>
          )}
        </div>

        {providerOptions.length > 1 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-4">
            <span className="mr-1 text-xs font-semibold uppercase tracking-[0.2em] text-content-muted">
              Provider
            </span>
            {providerOptions.map((option) => {
              const active = selectedProviders.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleProvider(option.value)}
                  aria-pressed={active}
                  className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    active ? PROVIDER_CHIP_CLASSES[option.value] : INACTIVE_CHIP_CLASS
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`size-2 rounded-full ${PROVIDER_ICON_CLASSES[option.value]}`}
                  />
                  {option.label}
                </button>
              );
            })}
            {selectedProviders.length > 0 && (
              <button
                type="button"
                onClick={clearProviders}
                className="min-h-11 px-2 text-xs text-content-muted hover:text-content-primary"
              >
                Clear provider
              </button>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-4">
          <span className="mr-1 text-xs font-semibold uppercase tracking-[0.2em] text-content-muted">
            Contract status
          </span>
          {CONTRACT_STATUS_FILTERS.map((option) => {
            const active = selectedStatuses.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleContractStatus(option.value)}
                className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active ? STATUS_CHIP_CLASSES[option.value] : INACTIVE_CHIP_CLASS
                }`}
              >
                {option.label}
              </button>
            );
          })}
          {selectedStatuses.length > 0 && (
            <button
              type="button"
              onClick={clearContractStatuses}
              className="min-h-11 px-2 text-xs text-content-muted hover:text-content-primary"
            >
              Clear status
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-4">
          <span className="mr-1 text-xs font-semibold uppercase tracking-[0.2em] text-content-muted">
            Claimed
          </span>
          <button
            type="button"
            onClick={() => setClaimedFilter("all")}
            aria-pressed={claimedFilter === "all"}
            className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              claimedFilter === "all" ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS
            }`}
          >
            All
          </button>
          {CLAIMED_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setClaimedFilter(option.value)}
              aria-pressed={claimedFilter === option.value}
              className={`min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                claimedFilter === option.value ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </FilterCard>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-default bg-surface-raised px-4 py-3">
        {loading ? (
          <p className="text-sm text-content-muted">Loading agents…</p>
        ) : data ? (
          <>
            <p className="text-sm text-content-primary">
              <span className="text-lg font-semibold tabular-nums">
                {data.pagination.total.toLocaleString()}
              </span>{" "}
              <span className="text-content-secondary">
                {data.pagination.total === 1 ? "agent" : "agents"}
                {hasActiveFilters ? " matching filters" : ""}
              </span>
            </p>
            {data.pagination.total > 0 && (
              <p className="text-xs text-content-muted">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </p>
            )}
          </>
        ) : null}
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {loading ? (
        <AgentsListSkeleton />
      ) : (
        <div className="overflow-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
          <table className="w-full text-sm min-w-[1280px]">
            <thead>
              <tr>
                <SortableHeader label="Agent" column="name" />
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  Organization
                </th>
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  NPN
                </th>
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  Carriers
                </th>
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  Upline
                </th>
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  Claimed
                </th>
                <SortableHeader label="Contracts" column="contracts" />
                <th className="text-left py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  Registered
                </th>
                <SortableHeader label="Updated" column="updated" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((item) => {
                const carriers = item.carriers;
                const visibleCarriers = carriers.slice(0, MAX_CARRIER_PILLS);
                const hiddenCarrierCount = carriers.length - visibleCarriers.length;
                const submissionStatus = item.latestSubmission?.status;
                const showSubmissionStatus =
                  submissionStatus && !isSubmissionAutofillComplete(submissionStatus);

                return (
                  <tr key={item.id} className="border-t border-border-subtle align-top">
                    <td className="py-3 pr-3 max-w-[240px]">
                      <Link
                        to={`/dashboard/agents/${item.id}`}
                        className="block truncate text-xs font-medium text-content-primary hover:underline"
                      >
                        {item.displayName || item.email}
                      </Link>
                      {item.displayName && (
                        <p className="mt-0.5 truncate text-[11px] leading-snug text-content-secondary">
                          {item.email}
                        </p>
                      )}
                    </td>
                    <td className="py-3 pr-3 max-w-[180px]">
                      {item.organization ? (
                        <span className="block truncate text-xs font-medium text-content-primary">
                          {item.organization.brandName}
                        </span>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {item.npn ? (
                        <button
                          type="button"
                          onClick={() => copyNpn(item.id, item.npn!)}
                          className="min-h-11 px-2 font-mono text-xs text-content-primary hover:text-accent"
                          aria-label={`Copy NPN ${item.npn}`}
                          title="Copy NPN"
                        >
                          {copiedNpnId === item.id ? "Copied" : item.npn}
                        </button>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 max-w-[220px]">
                      <div className="space-y-1.5">
                        {carriers.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {visibleCarriers.map((carrier) => (
                              <Badge key={carrier} variant="outline" className="max-w-[140px] truncate">
                                {carrierLabel(carrier)}
                              </Badge>
                            ))}
                            {hiddenCarrierCount > 0 && (
                              <Badge variant="outline">+{hiddenCarrierCount}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-content-muted">—</span>
                        )}
                        {showSubmissionStatus && (
                          <Badge variant={submissionStatusVariant(submissionStatus)}>
                            {formatSubmissionStatusForAgentProfile(submissionStatus)}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-3 max-w-[180px]">
                      {item.upline ? (
                        <Link
                          to={`/dashboard/agents/${item.upline.id}`}
                          className="block truncate text-xs text-content-primary hover:underline"
                          title={item.upline.email}
                        >
                          {item.upline.displayName}
                        </Link>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <ClaimedIcon claimed={item.claimed} />
                    </td>
                    <td className="py-3 pr-3">
                      <div className="space-y-1.5">
                        {item.contractStats.total > 0 && (
                          <p className="text-xs text-content-muted">
                            {item.contractStats.total} total
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {item.contractStats.active > 0 && (
                            <StatusBadge status="ACTIVE" size="sm" />
                          )}
                          {item.contractStats.pending > 0 && (
                            <StatusBadge status="PENDING" size="sm" />
                          )}
                          {item.contractStats.nigo > 0 && (
                            <StatusBadge status="NIGO" size="sm" />
                          )}
                          {item.contractStats.rejected > 0 && (
                            <StatusBadge status="REJECTED" size="sm" />
                          )}
                          {item.contractStats.total === 0 && (
                            <span className="text-xs text-content-muted">None</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">
                      <p className="text-content-primary">{new Date(item.createdAt).toLocaleDateString()}</p>
                      <p className="text-xs text-content-muted">{formatRelativeDate(item.createdAt)}</p>
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap text-content-secondary">
                      {new Date(item.updatedAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(data?.items ?? []).length === 0 && (
            <p className="py-8 text-center text-sm text-content-muted">
              {hasActiveFilters ? "No agents match the current filters." : "No agents found."}
            </p>
          )}

          <div className="flex items-center justify-between mt-4 text-xs text-content-muted">
            <span>
              Page {data?.pagination.page ?? 1} / {data?.pagination.totalPages ?? 1}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={(data?.pagination.page ?? 1) <= 1}
                onClick={() =>
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("page", String(Math.max(1, (data?.pagination.page ?? 1) - 1)));
                    return next;
                  })
                }
              >
                Prev
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={(data?.pagination.page ?? 1) >= (data?.pagination.totalPages ?? 1)}
                onClick={() =>
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("page", String((data?.pagination.page ?? 1) + 1));
                    return next;
                  })
                }
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCreateAgent && (
        <CreateAgentModal
          onClose={() => setShowCreateAgent(false)}
          onCreated={() => loadAgents()}
        />
      )}
    </section>
  );
}
